import { settingsTabTypes } from "@/components/apps/Settings";
import { wallpapers } from "@/lib/constant";
import { WidgetType } from "@/lib/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface SettingsState {
  theme: "light" | "dark";
  widgetPanel: boolean;
  activeTab: settingsTabTypes;
  backgroundImage: string;
  customWalls: string[];
  notificationOnMusicPlay: boolean;
  colorPalette: string;
  dynamicDock: boolean;
  windowControlButtonsOnRight: boolean;
  isChatNotificationOn: boolean;
  widgets: WidgetType[];
  setTheme: (theme: "light" | "dark") => void;
  toggleWidgetPanel: () => void;
  setActiveTab: (tab: settingsTabTypes) => void;
  setBackgroundImage: (backgroundImage: string) => void;
  addCustomWall: (wallpaper: string) => void;
  removeCustomWall: (wallpaper: string) => void;
  setNotificationOnMusicPlay: (getNotificationOnMusicPlay: boolean) => void;
  setDynamicDock: (dynamicDock: boolean) => void;
  setWindowControlButtons: (windowControlButtonsOnRight: boolean) => void;
  setColorPalette: (colorPalette: string) => void;
  setIsChatNotificationOn: (isChatNotificationOn: boolean) => void;
  addWidget: (widget: WidgetType) => void;
  removeWidget: (widget: WidgetType) => void;
}

const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      theme: "light",
      activeTab: "general",
      backgroundImage: wallpapers[5],
      dynamicDock: false,
      windowControlButtonsOnRight: false,
      colorPalette: "#dc2626",
      isChatNotificationOn: true,
      notificationOnMusicPlay: true,
      widgetPanel: true,
      widgets: ["Music", "Quotes"],
      customWalls: [],
      addWidget: (widget: WidgetType) =>
        set((state) => ({ widgets: [...state.widgets, widget] })),
      removeWidget: (widget: WidgetType) =>
        set((state) => ({
          widgets: state.widgets.filter((widgetName) => widgetName !== widget),
        })),
      setIsChatNotificationOn(isChatNotificationOn) {
        set({ isChatNotificationOn });
      },
      setColorPalette(colorPalette) {
        set({ colorPalette });
      },
      setTheme: (theme: "light" | "dark") => set({ theme }),
      setActiveTab: (tab: settingsTabTypes) => set({ activeTab: tab }),
      setBackgroundImage: (backgroundImage: string) => set({ backgroundImage }),
      setDynamicDock: (dynamicDock: boolean) => set({ dynamicDock }),
      setWindowControlButtons: (windowControlButtonsOnRight: boolean) =>
        set({ windowControlButtonsOnRight }),
      addCustomWall: (wallpaper: string) =>
        set((state) => ({ customWalls: [...state.customWalls, wallpaper] })),
      removeCustomWall: (wallpaper: string) =>
        set((state) => ({
          customWalls: state.customWalls.filter((wall) => wall !== wallpaper),
        })),
      setNotificationOnMusicPlay(notificationOnMusicPlay) {
        set({ notificationOnMusicPlay });
      },
      toggleWidgetPanel: () =>
        set((state) => ({ widgetPanel: !state.widgetPanel })),
    }),
    {
      name: "settings",
    }
  )
);

export default useSettingsStore;
